import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Offcanvas, Form } from "react-bootstrap";
import { MdArrowBack } from "react-icons/md";
import * as apicall from "../../components/Apicall";
import Skeleton from "../../components/Loader/Skeleton";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { Formik } from "formik";
import * as Yup from "yup";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import CurrencyDropdown, { CURRENCIES } from "../Dashboard/CurrencyDropdown";

// Slot lengths (in minutes) a vendor can pick for their booking slots.
const SLOT_DURATION_OPTIONS = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "1 hour", value: 60 },
  { label: "1 hour 30 minutes", value: 90 },
  { label: "2 hours", value: 120 }
];

// "14:30" -> "02:30 PM"
const formatTime = (time) => {
  if (!time) return "";
  const [hourStr, minute] = time.split(":");
  const hour = Number(hourStr);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(displayHour).padStart(2, "0")}:${minute} ${period}`;
};

// Shop images can come back either as an array or as the raw comma separated
// column value, so normalise both into a plain list of URLs.
const toImageList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value || value === "null") return [];
  return String(value)
    .split(",")
    .map((img) => img.trim())
    .filter(Boolean);
};

const formatSlotDuration = (minutes) => {
  if (!minutes) return "";
  const match = SLOT_DURATION_OPTIONS.find((o) => o.value === Number(minutes));
  return match ? match.label : `${minutes} minutes`;
};

const ViewProfile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditSidebar, setShowEditSidebar] = useState(false);
  const [fallbackColor, setFallbackColor] = useState("");
  const [removedImage, setRemovedImage] = useState(false);
  const currencyCode = userData?.personalDetails?.currency;
  const loginType = localStorage.getItem("login_type");
  const currencyObj = CURRENCIES.find((c) => c.code === currencyCode);

  const [isFocused, setIsFocused] = useState(false);
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";

    // Example: assuming first 2 digits = country code (India = 91)
    if (phone.length > 10) {
      const countryCode = phone.slice(0, phone.length - 10);
      const number = phone.slice(-10);
      return `+${countryCode} ${number}`;
    }

    return phone;
  };
  const getRandomColor = () => {
    const colors = [
      "#FFE4E1", // Misty Rose
      "#E6E6FA", // Lavender
      "#F0FFF0", // Honeydew
      "#F0F8FF", // Alice Blue
      "#FFF0F5", // Lavender Blush
      "#F5F5DC", // Beige
      "#E0FFFF", // Light Cyan
      "#FFE4B5", // Moccasin
      "#F0FFFF", // Azure
      "#F8F8FF" // Ghost White
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name.split(" ").filter((part) => part.length > 0);
    if (nameParts.length === 0) return "";
    if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await apicall.get("ezpMyProfile");
      const profile = response.data[0];
      const transformedData = {
        personalDetails: {
          fullName: `${profile.first_name}`.trim(),
          gender: profile.gender === 1 ? "Male" : "Female",
          currency: profile.currency || "",
          totalExperience: profile.practice_start
            ? calculateExperience(profile.practice_start)
            : "",
          id: profile?.id,
          vendor_id: profile?.vendor_id,
          State: profile?.state_name,
          Country: profile?.country_name,
          phone_number: profile?.phone_number,
          image: profile?.image,
          email: profile?.email,
          address: profile?.address,
          profile_image: profile?.profile_image,
          city_name: profile?.city_name,
          industry: profile?.industry,
          latitude: profile?.latitude,
          longitude: profile?.longitude
        },
        addressDetails: {
          address: profile.address || "",
          city: profile.city_name || "",
          state: profile.state_name || "",
          country: profile.country_name || "",
          latitude: profile.latitude ?? "",
          longitude: profile.longitude ?? ""
        },
        contactDetails: {
          phoneNumber: profile.phone_number || "",
          email: profile.email || ""
        },
        bookingHours: {
          startTime: profile.start_time || "",
          endTime: profile.end_time || "",
          slotDuration: profile.slot_duration || ""
        },
        profile_image: profile.profile_image || "",
        image: profile.image || "",
        officeImages: toImageList(profile.office_images),
        website_link: profile.website_link || "",
        notes: profile.notes || "",
        tax: profile?.tax,
        taxNo: profile?.tax_no
      };

      setUserData(transformedData);
      setFallbackColor(getRandomColor());
      setError(null);
    } catch (err) {
      console.error("Error fetching profile data:", err);
      setError("Failed to load profile data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUserData();
  }, []);
  const handleBack = () => {
    navigate(-1);
  };

  const handleEditProfile = () => {
    setShowEditSidebar(true);
  };

  const calculateExperience = (practiceStart) => {
    if (!practiceStart) return "0";
    const startDate = new Date(practiceStart);
    const currentDate = new Date();
    const years = currentDate.getFullYear() - startDate.getFullYear();
    return `${years} Years Experience`;
  };

  const EditProfileSidebar = ({
    showEditSidebar,
    setShowEditSidebar,
    userData
  }) => {
    const [formData, setFormData] = useState({
      // Vendor name lives entirely in first_name (no last_name) - keep the
      // whole thing, not just the first word, or saving the profile for any
      // other change would overwrite "Blue Dream Auto Maint" with "Blue".
      first_name: userData?.personalDetails.fullName || "",
      gender: userData?.personalDetails.gender === "Male" ? 1 : 2,
      currencyCode: userData?.personalDetails.currency || "USD",
      address: userData?.addressDetails.address || "",
      latitude: userData?.addressDetails.latitude ?? "",
      longitude: userData?.addressDetails.longitude ?? "",
      city_id: "",
      state_id: "",
      country_id: "",
      phone_number: userData?.contactDetails.phoneNumber || "",
      start_time: userData?.bookingHours.startTime || "",
      end_time: userData?.bookingHours.endTime || "",
      slot_duration: userData?.bookingHours.slotDuration || ""
    });
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCalendar, setShowCalendar] = useState(false);
    const [holidayRange, setHolidayRange] = useState([null, null]);
    const [unavailableDates, setUnavailableDates] = useState([]);
    const calendarRef = useRef(null);
    const profileSchema = Yup.object().shape({
      start_time: Yup.string().required("Start time is required"),
      end_time: Yup.string()
        .required("End time is required")
        .test(
          "after-start",
          "End time must be later than start time",
          function (value) {
            const { start_time } = this.parent;
            if (!start_time || !value) return true;
            return value > start_time;
          }
        ),
      slot_duration: Yup.number(),
      first_name: Yup.string().required("Full name is required"),
      // city_id: Yup.string().when([], function (_, context) {
      //   const cities = context && context.cities;
      //   if (cities && cities.length > 0) {
      //     return Yup.string().required('City is required');
      //   }
      //   return Yup.string();
      // }),
      latitude: Yup.string().test(
        "is-valid-latitude",
        "Latitude must be between -90 and 90",
        (value) => {
          if (!value) return true;
          const numValue = parseFloat(value);
          return !isNaN(numValue) && numValue >= -90 && numValue <= 90;
        }
      ),
      longitude: Yup.string().test(
        "is-valid-longitude",
        "Longitude must be between -180 and 180",
        (value) => {
          if (!value) return true;
          const numValue = parseFloat(value);
          return !isNaN(numValue) && numValue >= -180 && numValue <= 180;
        }
      )
    });
    const fetchCountries = async () => {
      try {
        const response = await apicall.get("ezp_country");

        if (response?.data) {
          // Handle both array and object responses
          const countriesData = Array.isArray(response.data)
            ? response.data
            : [response.data];
          setCountries(countriesData);
          return countriesData;
        }
        return [];
      } catch (error) {
        console.error("Error fetching countries:", error);
        return [];
      }
    };

    const fetchStates = async (countryId) => {
      if (!countryId) {
        return [];
      }
      try {
        const response = await apicall.get(`ezp_state/${countryId}`);
        if (response?.data) {
          const statesData = Array.isArray(response.data)
            ? response.data
            : [response.data];
          setStates(statesData);
          return statesData;
        }
        setStates([]);
        return [];
      } catch (error) {
        console.error("Error fetching states:", error);
        setStates([]);
        return [];
      }
    };

    const fetchCities = async (stateId) => {
      if (!stateId) {
        return [];
      }
      try {
        const response = await apicall.get(`ezp_city/${stateId}`);
        if (response?.data) {
          const citiesData = Array.isArray(response.data)
            ? response.data
            : [response.data];
          setCities(citiesData);
          return citiesData;
        }
        setCities([]);
        return [];
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
        return [];
      }
    };

    const fetchHolidays = async (userId) => {
      try {
        const response = await apicall.get(
          `get_vendor_holidays?user_id=${userId}`
        );
        setUnavailableDates(response.data || []);
      } catch (error) {
        console.error("Error fetching holidays:", error);
        toast.error("Failed to fetch holidays");
      }
    };

    const formatLocalDate = (date) => {
      const d = new Date(date);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    const unavailableDatesObj =
      unavailableDates?.map((item) => new Date(item.date)) || [];

    const handleSaveHoliday = async () => {
      let [start, end] = holidayRange;
      if (!start) {
        toast.error("Please select at least one date");
        return;
      }
      if (!end) end = start;

      const vendorUserId = userData?.personalDetails?.id;
      const payload = {
        user_id: vendorUserId,
        holiday_start: formatLocalDate(start),
        holiday_end: formatLocalDate(end),
        created_by: vendorUserId
      };

      try {
        const res = await apicall.post(payload, "add_vendor_holiday");
        if (res.success) {
          toast.success("Holiday saved successfully");
          setShowCalendar(false);
          setHolidayRange([null, null]);
          fetchHolidays(vendorUserId);
        } else {
          toast.error(res.error || res.message || "Failed to save holiday");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to save holiday");
      }
    };

    useEffect(() => {
      if (showEditSidebar && userData?.personalDetails?.id) {
        fetchHolidays(userData.personalDetails.id);
      }
    }, [showEditSidebar]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          calendarRef.current &&
          !calendarRef.current.contains(event.target)
        ) {
          setShowCalendar(false);
        }
      };
      if (showCalendar) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showCalendar]);

    // Initial data load
    useEffect(() => {
      const initializeData = async () => {
        try {
          setIsLoading(true);
          // Fetch countries first
          const countriesData = await fetchCountries();
          if (countriesData.length > 0) {
            // Find country by name first
            const currentCountry = countriesData.find((c) => {
              const countryName = c.country_name || c.name;
              const searchName = userData?.addressDetails?.country;
              return countryName?.toLowerCase() === searchName?.toLowerCase();
            });

            if (currentCountry) {
              const countryId = currentCountry.id || currentCountry.country_id;
              setFormData((prev) => ({ ...prev, country_id: countryId }));
              // Fetch states for the selected country
              const statesData = await fetchStates(countryId);
              if (statesData.length > 0) {
                // Log the current state name we're looking for
                // Find state by name
                const currentState = statesData.find((s) => {
                  const stateName = s.state_name || s.name;
                  const searchName = userData?.addressDetails?.state;
                  return stateName?.toLowerCase() === searchName?.toLowerCase();
                });
                if (currentState) {
                  const stateId = currentState.id || currentState.state_id;
                  setFormData((prev) => ({ ...prev, state_id: stateId }));
                  // Fetch cities for the selected state
                  const citiesData = await fetchCities(stateId);
                  if (citiesData.length > 0) {
                    // Find city by name
                    const currentCity = citiesData.find((c) => {
                      const cityName = c.city_name || c.name;
                      const searchName = userData?.addressDetails?.city;
                      return (
                        cityName?.toLowerCase() === searchName?.toLowerCase()
                      );
                    });
                    if (currentCity) {
                      const cityId = currentCity.id || currentCity.city_id;
                      setFormData((prev) => ({ ...prev, city_id: cityId }));
                    }
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error("Error initializing data:", error);
        } finally {
          setIsLoading(false);
        }
      };
      if (userData) {
        initializeData();
      }
    }, [userData]);

    const handleSubmit = async (values) => {
      // Nothing changed — don't hit the API or reload the page for a no-op
      // save, just let the user know.
      const hasFieldChange = Object.keys(formData).some(
        (key) => String(values[key] ?? "") !== String(formData[key] ?? "")
      );
      if (!hasFieldChange) {
        toast.info("No changes detected");
        return;
      }
      // Prepare the payload
      const payload = {
        first_name: values.first_name,
        gender: values.gender,
        currency: values.currencyCode,
        address: values.address,
        // Sent to the mobile app so it can map the shop — blank clears the pin.
        latitude: values.latitude === "" ? "" : String(values.latitude).trim(),
        longitude:
          values.longitude === "" ? "" : String(values.longitude).trim(),
        country_id: values.country_id,
        state_id: values.state_id,
        city_id: cities && cities.length > 0 ? values.city_id : null,
        phone_number: values.phone_number,
        start_time: values.start_time || "",
        end_time: values.end_time || "",
        slot_duration: values.slot_duration || ""
      };

      if (values.phone_number && values.country_id) {
        const selectedCountry = countries.find(
          (c) => c.id == values.country_id
        );

        const cleanPhone = values.phone_number.replace(/\D/g, "");
        const cleanCode = (selectedCountry?.telephone_code || "").replace(
          /\D/g,
          ""
        );

        if (!cleanPhone.startsWith(cleanCode)) {
          toast.error("Mobile number must match selected country");
          return;
        }

        const localNumber = cleanPhone.slice(cleanCode.length);

        if (cleanCode === "91") {
          if (localNumber.length !== 10) {
            toast.error("India number must be exactly 10 digits");
            return;
          }
        }

        if (cleanCode === "971") {
          if (localNumber.length !== 9) {
            toast.error("UAE number must be exactly 9 digits");
            return;
          }
        }
      }

      try {
        const response = await apicall.post(payload, "ezpUpdateUser");
        if (response.status === 200) {
          toast.success("Profile updated successfully", { autoClose: 1500 });
          setShowEditSidebar(false);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          toast.error(
            response?.message || "Failed to update profile. Please try again."
          );
        }
      } catch (error) {
        toast.error("Failed to update profile. Please try again.");
      }
    };

    const SidebarSkeleton = () => {
      return (
        <div className="p-4">
          <div className="mb-4">
            <Skeleton width="120px" height="24px" marginBottom="16px" />
            {[...Array(5)].map((_, index) => (
              <div key={index} className="mb-3">
                <Skeleton width="100px" height="20px" marginBottom="8px" />
                <Skeleton width="100%" height="38px" />
              </div>
            ))}
          </div>

          <div className="mb-4">
            <Skeleton width="120px" height="24px" marginBottom="16px" />
            {[...Array(4)].map((_, index) => (
              <div key={index} className="mb-3">
                <Skeleton width="100px" height="20px" marginBottom="8px" />
                <Skeleton width="100%" height="38px" />
              </div>
            ))}
          </div>

          <div className="mb-4">
            <Skeleton width="120px" height="24px" marginBottom="16px" />
            {[...Array(2)].map((_, index) => (
              <div key={index} className="mb-3">
                <Skeleton width="100px" height="20px" marginBottom="8px" />
                <Skeleton width="100%" height="38px" />
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <Offcanvas
        show={showEditSidebar}
        onHide={() => setShowEditSidebar(false)}
        placement="end"
        style={{ width: "100%", maxWidth: "500px" }}
        // className="d-flex flex-column"
      >
        <Offcanvas.Header closeButton className="custom-border-bottom p-4">
          <Offcanvas.Title className="page-heading">
            Edit Profile
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 d-flex flex-column">
          {isLoading ? (
            <SidebarSkeleton />
          ) : (
            <div className="flex-grow-1 overflow-auto p-4">
              <Formik
                initialValues={formData}
                validationSchema={profileSchema}
                onSubmit={handleSubmit}
                enableReinitialize
                validationContext={{ cities }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  setFieldError
                }) => (
                  <Form
                    onSubmit={handleSubmit}
                    className="d-flex flex-column h-100"
                    style={{ height: "100%" }}
                  >
                    <div
                      style={{
                        flex: 1,
                        // overflowY: "auto",
                        // padding: "16px",
                        paddingBottom: "65px"
                      }}
                    >
                      <div className="mb-4">
                        <h6 className="profile-section-heading">
                          Profile Details
                        </h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="first_name"
                            value={values.first_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.first_name && errors.first_name}
                            className="form-input"
                          />
                          {touched.first_name && errors.first_name && (
                            <div className="invalid-feedback d-block">
                              {errors.first_name}
                            </div>
                          )}
                        </Form.Group>

                        {/* <Form.Group className="mb-3">
                        <Form.Label>Gender</Form.Label>
                        <Form.Select
                          name="gender"
                          value={values.gender}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.gender && errors.gender}
                          className="form-input"
                        >
                          <option value={1}>Male</option>
                          <option value={2}>Female</option>
                        </Form.Select>
                        {touched.gender && errors.gender && (
                          <div className="invalid-feedback d-block">
                            {errors.gender}
                          </div>
                        )}
                      </Form.Group> */}
                      </div>

                      <div className="mb-4">
                        <h6 className="profile-section-heading">
                          Address Details
                        </h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.address && errors.address}
                            className="form-input"
                          />
                          {touched.address && errors.address && (
                            <div className="invalid-feedback d-block">
                              {errors.address}
                            </div>
                          )}
                        </Form.Group>
                        <div className="row">
                          <div className="col-6">
                            <Form.Group className="mb-3">
                              <Form.Label>Latitude</Form.Label>
                              <Form.Control
                                type="text"
                                name="latitude"
                                placeholder="e.g. 13.0827"
                                value={values.latitude}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.latitude && errors.latitude}
                                className="form-input"
                              />
                              {touched.latitude && errors.latitude && (
                                <div className="invalid-feedback d-block">
                                  {errors.latitude}
                                </div>
                              )}
                            </Form.Group>
                          </div>
                          <div className="col-6">
                            <Form.Group className="mb-3">
                              <Form.Label>Longitude</Form.Label>
                              <Form.Control
                                type="text"
                                name="longitude"
                                placeholder="e.g. 80.2707"
                                value={values.longitude}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.longitude && errors.longitude
                                }
                                className="form-input"
                              />
                              {touched.longitude && errors.longitude && (
                                <div className="invalid-feedback d-block">
                                  {errors.longitude}
                                </div>
                              )}
                            </Form.Group>
                          </div>
                        </div>
                        <Form.Group className="mb-3">
                          <Form.Label>Country</Form.Label>
                          <AppDropdown
                            value={values.country_id}
                            options={countries.map((c) => ({
                              label: c.name || c.country_name,
                              value: c.id
                            }))}
                            onChange={async (e) => {
                              const countryId = e.value;
                              setFieldValue("country_id", countryId);
                              if (countryId) {
                                await fetchStates(countryId);
                                setFieldValue("state_id", "");
                                setFieldValue("city_id", "");
                                setCities([]);
                              } else {
                                setStates([]);
                                setCities([]);
                                setFieldValue("state_id", "");
                                setFieldValue("city_id", "");
                              }
                            }}
                            error={touched.country_id && errors.country_id}
                            placeholder="Select Country"
                          />
                          {touched.country_id && errors.country_id && (
                            <div className="invalid-feedback d-block">
                              {errors.country_id}
                            </div>
                          )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>State</Form.Label>
                          <AppDropdown
                            value={values.state_id}
                            options={states.map((s) => ({
                              label: s.name,
                              value: s.id
                            }))}
                            onChange={async (e) => {
                              const stateId = e.value;
                              setFieldValue("state_id", stateId);
                              if (stateId) {
                                const fetchedCities =
                                  await fetchCities(stateId);
                                if (
                                  !fetchedCities ||
                                  fetchedCities.length === 0
                                ) {
                                  setFieldValue("city_id", null);
                                } else {
                                  setFieldValue("city_id", "");
                                }
                              } else {
                                setCities([]);
                                setFieldValue("city_id", null);
                              }
                            }}
                            error={touched.state_id && errors.state_id}
                            placeholder="Select State"
                          />
                          {touched.state_id && errors.state_id && (
                            <div className="invalid-feedback d-block">
                              {errors.state_id}
                            </div>
                          )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>City</Form.Label>
                          <AppDropdown
                            value={values.city_id}
                            options={cities.map((c) => ({
                              label: c.name,
                              value: c.id
                            }))}
                            onChange={(e) => setFieldValue("city_id", e.value)}
                            error={touched.city_id && errors.city_id}
                            placeholder="Select City"
                          />
                          {touched.city_id && errors.city_id && (
                            <div className="invalid-feedback d-block">
                              {errors.city_id}
                            </div>
                          )}
                        </Form.Group>
                        {/* <Form.Group className="mb-3"> */}

                        <Form.Label>Currency</Form.Label>
                        <CurrencyDropdown
                          currencyCode={values.currencyCode}
                          currencyChange={(data) =>
                            setFieldValue("currencyCode", data)
                          }
                        />
                      </div>

                      <div className="mb-4">
                        <h6 className="profile-section-heading">
                          Contact Details
                        </h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Mobile Number</Form.Label>
                          <PhoneInput
                            country={"ae"}
                            onlyCountries={["ae", "in"]}
                            //                            placeholder={
                            //   values.currencyCode === "AED"
                            //     ? "50 123 4567"
                            //     : "98765 43210"
                            // }
                            value={values.phone_number}
                            onChange={(value) => {
                              const cleanedValue = value.replace(/\D/g, "");
                              setFieldValue("phone_number", cleanedValue);
                            }}
                            disabled={loginType === "phone"}
                            inputStyle={{
                              width: "100%",
                              height: "40px",
                              backgroundColor: "#F8F9FA",
                              borderColor: "#E9ECEF"
                            }}
                            inputProps={{
                              name: "phone_number",
                              required: true
                              // disabled: loginType === "phone",
                              // disabled: true
                            }}
                            // disabled
                          />
                        </Form.Group>
                      </div>

                      <div className="mb-4 mt-4">
                        <h6 className="profile-section-heading">
                          {Number(userData?.personalDetails?.industry) === 1
                            ? "Availability Hours"
                            : "Booking Hours"}
                        </h6>

                        <div className="row">
                          <div className="col-6">
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Start Time
                                <span style={{ color: "red" }}>*</span>
                              </Form.Label>
                              <Form.Control
                                type="time"
                                name="start_time"
                                value={values.start_time}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.start_time && errors.start_time
                                }
                                className="form-input"
                              />
                              {touched.start_time && errors.start_time && (
                                <div className="invalid-feedback d-block">
                                  {errors.start_time}
                                </div>
                              )}
                            </Form.Group>
                          </div>
                          <div className="col-6">
                            <Form.Group className="mb-3">
                              <Form.Label>
                                End Time
                                <span style={{ color: "red" }}>*</span>
                              </Form.Label>
                              <Form.Control
                                type="time"
                                name="end_time"
                                value={values.end_time}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.end_time && errors.end_time}
                                className="form-input"
                              />
                              {touched.end_time && errors.end_time && (
                                <div className="invalid-feedback d-block">
                                  {errors.end_time}
                                </div>
                              )}
                            </Form.Group>
                          </div>
                        </div>

                        <Form.Group className="mb-3">
                          <Form.Label>Slot Duration</Form.Label>
                          <AppDropdown
                            value={values.slot_duration}
                            options={SLOT_DURATION_OPTIONS}
                            onChange={(e) =>
                              setFieldValue("slot_duration", e.value)
                            }
                            error={
                              touched.slot_duration && errors.slot_duration
                            }
                            placeholder="Select Slot Duration"
                          />
                          {touched.slot_duration && errors.slot_duration && (
                            <div className="invalid-feedback d-block">
                              {errors.slot_duration}
                            </div>
                          )}
                        </Form.Group>
                      </div>

                      <div
                        className="p-3 mt-3 position-relative"
                        style={{
                          border: "1px solid #E3EBF8",
                          borderRadius: "12px",
                          background: "#FAFBFF"
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div style={{ fontWeight: 600, fontSize: "14px" }}>
                            Availability
                          </div>
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setShowCalendar(true);
                              setHolidayRange([null, null]);
                              fetchHolidays(userData?.personalDetails?.id);
                            }}
                          >
                            + Set Holiday
                          </Button>
                        </div>

                        {showCalendar && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            ref={calendarRef}
                            className="position-absolute bg-white shadow rounded p-2"
                            style={{ top: "60px", right: "0", zIndex: 1000 }}
                          >
                            <DatePicker
                              selectsRange
                              startDate={holidayRange[0]}
                              endDate={holidayRange[1]}
                              onChange={(dates) => setHolidayRange(dates)}
                              inline
                              minDate={new Date()}
                              highlightDates={[
                                {
                                  "react-datepicker__day--highlighted-holiday":
                                    unavailableDatesObj
                                }
                              ]}
                              dayClassName={(date) => {
                                const today = new Date();
                                if (
                                  date.toDateString() === today.toDateString()
                                ) {
                                  return "react-datepicker__day--today-custom";
                                }
                                if (
                                  holidayRange[0] &&
                                  !holidayRange[1] &&
                                  date.toDateString() ===
                                    holidayRange[0].toDateString()
                                ) {
                                  return "react-datepicker__day--selected-single";
                                }
                                return undefined;
                              }}
                            />
                            <div className="d-flex justify-content-between mt-2">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowCalendar(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={handleSaveHoliday}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="bg-white custom-border-top p-3 d-flex gap-3">
                        {" "}
                        <Button
                          variant="light"
                          onClick={() => setShowEditSidebar(false)}
                          className="custom-radius btn-outline-borders"
                          type="button"
                          style={{ flex: 1 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="danger"
                          className="btn-custom-danger custom-radius"
                          type="submit"
                          style={{ flex: 1 }}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    );
  };
  const handleNavigate = () => {
    navigate(`/book-service/vendor/${userData.personalDetails.vendor_id}`, {
      state: { personalDetails: userData.personalDetails }
    });
  };
  const ProfileSkeleton = () => {
    return (
      <div className="px-4 py-3">
        <div className="d-flex align-items-center mb-4">
          <Skeleton width="80px" height="24px" />
        </div>

        <div className="bg-white border-box p-4 rounded-4">
          <div className="d-flex gap-5">
            {/* Left Column - Profile Image Skeleton */}
            <div
              className="d-flex flex-column align-items-center"
              style={{ width: "258px" }}
            >
              <Skeleton width="258px" height="248px" marginBottom="16px" />
              <Skeleton width="121px" height="32px" />
            </div>

            {/* Right Column - Details Skeleton */}
            <div className="flex-grow-1">
              <div className="row g-4">
                {/* Personal Details Skeleton */}
                <div className="col-md-4">
                  <Skeleton width="120px" height="24px" marginBottom="16px" />
                  <div className="d-flex flex-column gap-3">
                    {[...Array(4)].map((_, index) => (
                      <div key={index}>
                        <Skeleton
                          width="80px"
                          height="16px"
                          marginBottom="8px"
                        />
                        <Skeleton width="140px" height="20px" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Address Details Skeleton */}
                <div className="col-md-4">
                  <Skeleton width="120px" height="24px" marginBottom="16px" />
                  <div className="d-flex flex-column gap-3">
                    {[...Array(4)].map((_, index) => (
                      <div key={index}>
                        <Skeleton
                          width="80px"
                          height="16px"
                          marginBottom="8px"
                        />
                        <Skeleton width="140px" height="20px" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Details Skeleton */}
                <div className="col-md-4">
                  <Skeleton width="120px" height="24px" marginBottom="16px" />
                  <div className="d-flex flex-column gap-3">
                    {[...Array(2)].map((_, index) => (
                      <div key={index}>
                        <Skeleton
                          width="80px"
                          height="16px"
                          marginBottom="8px"
                        />
                        <Skeleton width="140px" height="20px" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return <div className="p-4 text-danger">{error}</div>;
  }

  if (!userData) {
    return <div className="p-4">No profile data available</div>;
  }

  return (
    <div className="px-4 py-3">
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="link"
          className="back-button text-dark text-decoration-none p-0"
          onClick={handleBack}
        >
          <MdArrowBack size={20} /> BACK
        </Button>
      </div>

      <div className="bg-white border-box p-4 rounded-4">
        <div className="d-flex flex-column flex-lg-row gap-4">
          {/* Left Column - Profile Image */}
          <div
            className="d-flex flex-column align-items-center col-12 col-md-auto"
            style={{
              maxWidth: "258px",
              width: "100%",
              margin: "0 auto"
            }}
          >
            <div
              className="position-relative w-100"
              style={{
                paddingBottom: "100%", // Creates a 1:1 aspect ratio
                height: "0",
                minHeight: "200px",
                maxHeight: "258px"
              }}
            >
              {userData.profile_image ? (
                <img
                  src={
                    userData.profile_image.startsWith("https")
                      ? userData.profile_image
                      : userData.profile_image
                  }
                  alt={userData.personalDetails.fullName}
                  className="border-box profile-image-shadow w-100 h-100"
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    borderRadius: "20px",
                    objectFit: "cover",
                    minHeight: "200px",
                    maxHeight: "258px"
                  }}
                  // onError={(e) => {
                  //   e.target.style.display = "none";
                  //   e.target.nextElementSibling.style.display = "flex";
                  // }}
                />
              ) : null}
              {!userData.profile_image && (
                <div
                  className="border-box profile-image-shadow d-flex align-items-center justify-content-center"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: "20px",
                    backgroundColor: fallbackColor,
                    fontSize: "40px",
                    fontWeight: "600",
                    color: "#666"
                  }}
                >
                  {getInitials(userData.personalDetails.fullName)}
                </div>
              )}
              {/* <div
                className="border-box profile-image-shadow d-flex align-items-center justify-content-center w-100 h-100"
                 style={{
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        backgroundColor: fallbackColor,
        fontSize: "14px",
        fontWeight: "500",
        color: "#666",
      }}
                // style={{
                //   position: "absolute",
                //   top: "0",
                //   left: "0",
                //   borderRadius: "20px",
                //   backgroundColor: fallbackColor,
                //   display: userData.profile_image ? "none" : "flex",
                //   fontSize: "calc(32px + 2vw)",
                //   fontWeight: "500",
                //   color: "#666666",
                //   minHeight: "200px",
                //   maxHeight: "258px",
                // }}
              >
                {getInitials(userData.personalDetails.fullName)}
              </div> */}
            </div>
            <div className="d-flex justify-content-center gap-3 mt-3 mb-3 w-100">
              <Button
                variant="danger"
                onClick={handleEditProfile}
                className="btn-custom-danger custom-radius d-flex align-items-center justify-content-center"
                style={{
                  width: "121px",
                  height: "32px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                Edit Profile
              </Button>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="flex-grow-1">
            <div className="row g-4">
              {/* Personal Details */}
              <div className="col-12 col-md-6 col-lg-4">
                <h6 className="profile-section-heading">Profile Details</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="label mb-1">Full Name</div>
                    <div
                      className="content fw-bold text-dark text-break"
                      style={{
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        maxWidth: "100%"
                      }}
                    >
                      {userData.personalDetails.fullName}
                    </div>
                  </div>
                  <div>
                    {/* <div className="label mb-1">Gender</div>
                    <div className="content fw-bold text-dark text-break">
                      {userData.personalDetails.gender}
                    </div> */}
                    {/* <div className="label mb-1">Booking Link</div> */}
                    <div className="content fw-bold text-dark text-break">
                      {/* {userData?.personalDetails?.vendor_id && (
                        <span
                          onClick={() => {
                            const url = `/book-service/vendor/${userData.personalDetails.vendor_id}`;
                            localStorage.setItem(
                              "userDetails",
                              JSON.stringify(userData.personalDetails)
                            );
                            window.open(url, "_blank");
                          }}
                          style={{
                            color: "#dc3545",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {`/book-service/vendor/${userData.personalDetails.vendor_id}`}
                        </span>
                      )} */}

                      {userData?.personalDetails?.vendor_id && (
                        <button
                          className="btn btn-danger"
                          onClick={() => {
                            const url = `/book-service/vendor/${userData.personalDetails.vendor_id}`;
                            localStorage.setItem(
                              "userDetails",
                              JSON.stringify(userData.personalDetails)
                            );
                            window.open(url, "_blank");
                          }}
                        >
                          {/* Link for Customers */}
                          {Number(userData.personalDetails.industry) === 1
                            ? "Book Appointment"
                            : "Booking Link"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="label mb-1">Currency</div>
                    <div className="content fw-bold text-dark text-break">
                      {currencyObj?.symbol} {currencyObj?.code}
                    </div>
                  </div>
                  <div></div>
                </div>
              </div>

              {/* Address Details */}
              <div className="col-12 col-md-6 col-lg-4">
                <h6 className="profile-section-heading">Address Details</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="label mb-1">Address</div>
                    <div
                      className="content fw-bold text-dark text-break"
                      style={{
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        maxWidth: "100%"
                      }}
                    >
                      {userData.addressDetails.address || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">City</div>
                    <div className="content fw-bold text-dark text-break">
                      {userData.addressDetails.city}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">State</div>
                    <div className="content fw-bold text-dark text-break">
                      {userData.addressDetails.state}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Country</div>
                    <div className="content fw-bold text-dark text-break">
                      {userData.addressDetails.country}
                    </div>
                  </div>
                  <div className="d-flex gap-5">
                    <div>
                      <div className="label mb-1">Latitude</div>
                      <div className="content fw-bold text-dark text-break">
                        {userData.addressDetails.latitude || "-"}
                      </div>
                    </div>
                    <div>
                      <div className="label mb-1">Longitude</div>
                      <div className="content fw-bold text-dark text-break">
                        {userData.addressDetails.longitude || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="col-12 col-md-6 col-lg-4">
                <h6 className="profile-section-heading">Contact Details</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="label mb-1">Mobile Number</div>
                    <div className="content fw-bold text-dark text-break">
                      {formatPhoneNumber(userData.contactDetails.phoneNumber)}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Email</div>
                    <div
                      className="content fw-bold text-dark text-break"
                      style={{
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        maxWidth: "100%"
                      }}
                    >
                      {userData.contactDetails.email}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <h6 className="profile-section-heading">
                  {userData.personalDetails.industry === 1
                    ? "Availability Hours"
                    : "Booking Hours"}
                </h6>
                <div className="d-flex gap-5">
                  <div>
                    <div className="label mb-1">Start Time</div>
                    <div className="content fw-bold text-dark text-break">
                      {formatTime(userData.bookingHours.startTime) || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">End Time</div>
                    <div className="content fw-bold text-dark text-break">
                      {formatTime(userData.bookingHours.endTime) || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Slot Duration</div>
                    <div className="content fw-bold text-dark text-break">
                      {formatSlotDuration(userData.bookingHours.slotDuration) ||
                        "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Shop Images */}
              {userData.officeImages.length > 0 && (
                <div className="col-12">
                  <h6 className="profile-section-heading">Shop Images</h6>
                  <div className="d-flex flex-wrap gap-3">
                    {userData.officeImages.map((img, index) => (
                      <img
                        key={`${img}-${index}`}
                        src={img}
                        alt={`Shop ${index + 1}`}
                        onClick={() => window.open(img, "_blank")}
                        style={{
                          width: "120px",
                          height: "90px",
                          objectFit: "cover",
                          borderRadius: "12px",
                          border: "1px solid #E3EBF8",
                          cursor: "pointer"
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <EditProfileSidebar
        showEditSidebar={showEditSidebar}
        setShowEditSidebar={setShowEditSidebar}
        userData={userData}
      />
    </div>
  );
};

export default ViewProfile;
